import React, { useState } from 'react';
import { Globe, AlertCircle, ExternalLink } from 'lucide-react';

export interface IFrameWidgetConfig {
  url: string; // URL to embed or iframe HTML code
  backgroundColor?: string;
}

interface IFrameWidgetProps {
  config: IFrameWidgetConfig;
  width: number; // Grid columns (1-12)
  height: number; // Grid rows
}

// Extract src from iframe HTML code
function extractIframeSrc(input: string): string | null {
  // Check if input looks like HTML (contains < or >)
  if (input.includes('<iframe')) {
    // Try to extract src attribute from iframe tag
    const srcMatch = input.match(/src=["']([^"']+)["']/);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }
  }
  // Return original if it's a URL
  return input;
}

export function IFrameWidget({ config, width, height }: IFrameWidgetProps) {
  const { url: rawUrl = '', backgroundColor } = config || {};
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Extract URL from iframe code if needed
  const url = extractIframeSrc(rawUrl);

  // Dynamic sizing
  const area = width * height;
  const isSmall = area <= 12;
  const padding = isSmall ? 'p-2' : 'p-3';

  // No URL state
  if (!url) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <Globe size={48} className="text-gray-300 dark:text-gray-600 mb-2" />
        <div className="text-gray-400 dark:text-gray-500 text-sm text-center">
          Enter URL to embed content
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`w-full h-full border border-dashboard-widget rounded-lg shadow-sm ${padding} flex flex-col items-center justify-center`}
        style={{ backgroundColor: backgroundColor || undefined }}
      >
        <AlertCircle size={48} className="text-red-400 mb-2" />
        <div className="text-red-600 dark:text-red-400 text-sm text-center mb-3">
          Unable to load content
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3 max-w-xs">
          The website may not allow embedding
        </p>
        <button
          onClick={() => window.open(url, '_blank')}
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all"
        >
          <ExternalLink size={14} />
          Open in New Tab
        </button>
      </div>
    );
  }

  // IFrame display
  return (
    <div
      className="w-full h-full border border-dashboard-widget rounded-lg shadow-sm overflow-hidden relative"
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      {/* Loading state */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 z-10">
          <div className="text-gray-400 dark:text-gray-500 text-sm">Loading...</div>
        </div>
      )}

      {/* IFrame */}
      <iframe
        src={url}
        className="w-full h-full border-none"
        title="Embedded content"
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    </div>
  );
}
